/**
 * Gym Shorts theme combo — picker behaviour for combo landing pages.
 *
 * On a combo landing the pack FOLLOWS the selection instead of capping it:
 *   - ticking a design adds it (no FIFO bump) and moves the pack tile up,
 *     up to the largest tile (e.g. 6);
 *   - past the largest tile the customer may keep ticking up to
 *     data-max-total (default 10) — the headline then shows the largest
 *     tile's per-pair price × the selected count;
 *   - unticking moves the pack tile down, never below the smallest tile;
 *   - clicking a pack tile still fills up to / trims down to that size
 *     (the first designs in grid order are preselected on load).
 *
 * Works by overriding a few methods on the combo picker instance only
 * ([data-gs-combo-picker]); <gs-v4-design-picker> elsewhere keeps the stock
 * v4 behaviour from gym-shorts-v4.js. The coordinator is untouched: it asks
 * picker.isComplete() / getSelectedDesigns() and adds each pick as a line.
 */
(() => {
  if (window.__gsComboBound) return;
  window.__gsComboBound = true;

  const currentPicker = () => document.querySelector('[data-gs-combo-picker]');
  const packSelector = () => document.querySelector('[data-gs-v4-pack-selector]');
  const barLabel = () => document.querySelector('[data-gs-v4-size-bar]')?.dataset.selectedLabel;
  const count = (picker) => picker._selectedCount();

  // Re-entrancy guard: selections made while applying a pack change (fill,
  // prune) must not feed back into the pack-follows-selection sync.
  let applying = false;
  // True while syncPack moves the tile itself: that pack change mirrors the
  // selection, so it must not trigger fill (it would re-tick a design the
  // customer just unticked).
  let syncing = false;
  let headlineOverridden = false;

  function packBounds() {
    const packs = [...(packSelector()?.querySelectorAll('[data-pack]') || [])]
      .map(t => Number(t.dataset.pack)).filter(Number.isFinite);
    return { min: Math.min(...packs), max: Math.max(...packs) };
  }
  function maxTotal(picker) {
    return Number(picker.dataset.maxTotal) || 10;
  }

  // ---------------------------------------------------------------------------
  // Picker overrides (instance-level, combo picker only)
  // ---------------------------------------------------------------------------
  function patchPicker(picker) {
    if (picker.__gsComboPatched) return;
    picker.__gsComboPatched = true;

    // Ticking never bumps another design out — only the hard cap applies.
    picker.requestSelection = () => count(picker) + 1 <= maxTotal(picker);
    picker.canAddQty = (card, delta) => count(picker) + delta <= maxTotal(picker);

    // Pack change: trim to the chosen pack, except on the largest tile where
    // "N or more" is allowed up to the hard cap.
    picker._enforceBudget = function () {
      const limit = this.pack >= packBounds().max ? maxTotal(this) : this.pack;
      let safety = 50;
      while (count(this) > limit && safety-- > 0) {
        const picks = this._allSelectedCards();
        const last = picks[picks.length - 1];
        if (!last) break;
        if (last.qty > 1) {
          last.dataset.qty = String(last.qty - 1);
          last._updateQtyUI();
        } else {
          last._deselect();
        }
      }
    };

    picker.isComplete = () => count(picker) >= packBounds().min;

    const baseUpdateUI = picker._updateUI.bind(picker);
    picker._updateUI = function () {
      baseUpdateUI();
      const n = count(this);
      const { min } = packBounds();
      if (this.counterEl) {
        this.counterEl.textContent = `${n} selected`;
        this.counterEl.classList.toggle('is-complete', n >= min);
        this.counterEl.classList.toggle('is-incomplete', n < min);
      }
      if (this.promptEl) {
        const left = this.querySelectorAll('gs-v4-design-card[data-selected="false"]').length;
        this.promptEl.textContent = n < min
          ? `Pick at least ${min} designs`
          : left > 0 && n < maxTotal(this)
            ? 'Your combo is ready. Tick more designs to save more'
            : 'Your combo is ready';
      }
    };
  }

  // ---------------------------------------------------------------------------
  // Preselect: fill up to the current pack (load + pack tile clicks)
  // ---------------------------------------------------------------------------
  function unselected(picker) {
    return [...picker.querySelectorAll('gs-v4-design-card[data-selected="false"]')];
  }
  function fillToPack(picker) {
    const pack = Number(picker.pack) || 0;
    const label = barLabel();
    let safety = 20;
    while (count(picker) < pack && safety-- > 0) {
      // Prefer designs that stock the size-bar size, then any in-stock size.
      let card = label && unselected(picker).find(c => c._findSizeButton(label));
      let btn = card && card._findSizeButton(label);
      if (!card) {
        card = unselected(picker).find(c => c._findFirstAvailableSize());
        btn = card && card._findFirstAvailableSize();
      }
      if (card && btn) { card._select(btn); continue; }
      // Pool exhausted: extra quantity on the last pick.
      const picks = picker._allSelectedCards();
      const last = picks[picks.length - 1];
      if (!last || !picker.canAddQty(last, 1)) break;
      last.dataset.qty = String(last.qty + 1);
      last._updateQtyUI();
      last.dispatchSelectionChange();
    }
  }

  // A pick folded behind "+N MORE" would be invisible but still go to the
  // cart: unfold the overflow so every pick stays on screen.
  function revealHiddenPicks(picker) {
    const overflow = picker.querySelector('[data-gs-v4-overflow]');
    if (!overflow || !overflow.classList.contains('gs-v3-grid--hidden')) return;
    if (!overflow.querySelector('gs-v4-design-card[data-selected="true"]')) return;
    picker.querySelector('[data-gs-v4-toggle-expand]')?.click();
  }

  // ---------------------------------------------------------------------------
  // Pack follows selection + headline beyond the largest tile
  // ---------------------------------------------------------------------------
  function selectPack(n) {
    const sel = packSelector();
    if (!sel || sel.selected === n) return false;
    applying = true;
    syncing = true;
    try { sel.select(n); } finally { applying = false; syncing = false; }
    return true;
  }

  function syncPack(picker) {
    if (applying) return;
    const sel = packSelector();
    if (!sel) return;
    const n = count(picker);
    const { min, max } = packBounds();
    if (n >= min) selectPack(Math.min(n, max));
    if (n > max) {
      overrideHeadline(sel, n, max);
    } else if (headlineOverridden) {
      headlineOverridden = false;
      const tile = sel.querySelector(`[data-pack="${sel.selected}"]`);
      if (tile) sel._applyHeadline(tile);
    }
  }

  // "$28.00 each" stays (largest tile's per-pair price); the total line is
  // recomputed for the real count, compare = largest tile's compare per pair.
  function overrideHeadline(sel, n, max) {
    const tile = sel.querySelector(`[data-pack="${max}"]`);
    const total = sel.parentNode.querySelector('.gs-v41-price-total')
      || document.querySelector('.gs-v41-price-total');
    if (!tile || !total) return;
    sel._applyHeadline(tile);
    const priceCents = Number(tile.dataset.packPriceCents || 0);
    const compareCents = Number(tile.dataset.packCompareCents || 0);
    const fm = (tile.dataset.packPrice || '').match(/^(\D*)[\d.,]+(\D*)$/);
    const fmt = (c) => (fm ? fm[1] : '$') + (c / 100).toFixed(2) + (fm ? fm[2] : '');
    const totalCents = Math.round(priceCents / max) * n;
    total.textContent = 'Total ';
    const strong = document.createElement('strong');
    strong.textContent = fmt(totalCents);
    total.append(strong, ` for ${n} shorts`);
    if (compareCents > priceCents) {
      const s = document.createElement('s');
      s.className = 'gs-v41-price-total__compare';
      s.textContent = fmt(Math.round(compareCents / max) * n);
      total.append(' ', s);
    }
    sel._updateInstallments?.(totalCents, fmt);
    headlineOverridden = true;
  }

  // ---------------------------------------------------------------------------
  async function init() {
    if (!currentPicker()) return;
    await Promise.all(['gs-v4-design-picker', 'gs-v4-design-card', 'gs-v4-size-bar', 'gs-v4-1-pack-selector']
      .map(tag => customElements.whenDefined(tag)));
    const picker = currentPicker();
    patchPicker(picker);

    // Pack tile clicks go through select(); mark them so the prune/fill they
    // trigger doesn't bounce the pack back via syncPack.
    const sel = packSelector();
    if (sel && !sel.__gsComboWrapped) {
      sel.__gsComboWrapped = true;
      const baseSelect = sel.select.bind(sel);
      sel.select = (n) => {
        const was = applying;
        applying = true;
        try { baseSelect(n); } finally { applying = was; }
      };
    }

    applying = true;
    fillToPack(picker);
    applying = false;
    revealHiddenPicks(picker);
    picker._updateUI();

    document.addEventListener('gs-v4:pack-change', () => {
      const fromSync = syncing; // read now: the flag resets before the timeout
      setTimeout(() => onPackChange(fromSync), 0);
    });
    function onPackChange(fromSync) {
      const p = currentPicker();
      if (!p) return;
      if (!fromSync) {
        applying = true;
        fillToPack(p);
        applying = false;
      }
      revealHiddenPicks(p);
      syncPack(p);
      p._updateUI();
    }
    document.addEventListener('gs-v4:design-change', () => {
      const p = currentPicker();
      if (p) { syncPack(p); revealHiddenPicks(p); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
